import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { without, difference } from 'lodash';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';

import { useQueryChannelAccounts, useConnectedAccounts } from 'src/services';
import { SOCIAL_CHANNELS } from 'src/data/performance';
import { getChannelPerformanceLabel } from 'src/data/channel';
import ChannelIcon from 'src/components/ChannelIcon';
import { AccountsTable } from 'src/components/AccountsTable';
import { useHandlers } from './useHandlers';
import { useViewState } from './useViewState';
import { useStyles } from './useStyles';
import { useCustomFeatureLimitsData } from 'src/store';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { UPGRADE_DIALOG_ACTIONS_ORIGIN } from 'src/data/common';
import { useUpgradePopup } from 'src/services/popup';
import { ActionsMenu } from 'src/views/AdAccountsSelectionPopup/components/ActionsMenu';
import { FeatureLimitedProgressBar } from 'src/views/ChannelsResourcesPage/components/FeatureLimitedProgressBar';

export const AdAccountsSelectionPopup = ({ open, channel, onSubmit, onDisconnect, onClose }) => {
  const styles = useStyles();

  const {
    accounts,
    selectedIds,
    loadingAccounts,
    accountsSortField,
    accountsSortDirection,
    runningAccountIds,
    connectingToChannel,
  } = useViewState(channel);
  const { limit, currentCount, limitedAccountData } = useCustomFeatureLimitsData(
    FEATURE_LIMITS.resourcesRowsPerCustomer
  );
  const isFeatureLimited = limit && (!!limitedAccountData?.accountId || limit <= currentCount);
  const item = SOCIAL_CHANNELS.includes(channel) ? 'page' : 'account';
  const isAccountProcessing = runningAccountIds?.length > 0;
  const openUpgradePopup = useUpgradePopup({
    actionsOrigin: UPGRADE_DIALOG_ACTIONS_ORIGIN.cipPageConnectDialog,
  });
  const { handleSortChange } = useHandlers(channel);

  useQueryChannelAccounts(channel, connectingToChannel);

  const connectedAccounts = useConnectedAccounts();

  const handleAccountClick = useCallback(
    (accountId, isConnected) => {
      if (!isConnected && isFeatureLimited) {
        openUpgradePopup(FEATURE_LIMITS.resourcesRowsPerCustomer);
        return;
      }
      const selectedAccountIds = isConnected
        ? without(selectedIds, accountId)
        : [...selectedIds, accountId];
      const action = {
        channel,
        accountId,
        selectedAccountIds,
        action: isConnected ? 'disconnect' : 'connect',
      };

      onSubmit(action);
    },
    [selectedIds, onSubmit, channel, isFeatureLimited, openUpgradePopup]
  );

  return (
    <Dialog
      open={open}
      classes={{ paper: styles.root }}
      onClose={onClose}
      maxWidth="md"
      data-testid="AdAccountsSelectionPopup"
    >
      <DialogTitle className={styles.dialogTitle}>
        <Typography className={styles.popupTitle}>
          <ChannelIcon
            className={styles.channelIcon}
            channel={channel}
            meta
          />
          Select {getChannelPerformanceLabel(channel)} account to integrate with Anyword
        </Typography>
        <ActionsMenu onRemoveClick={onDisconnect} />
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <AccountsTable
          accounts={accounts}
          connectedAccountIds={difference(selectedIds, runningAccountIds)}
          onAccountClick={handleAccountClick}
          onSortChange={handleSortChange}
          sortField={accountsSortField}
          sortDirection={accountsSortDirection}
          item={item}
          channel={channel}
          limitedAccountData={limitedAccountData}
        loadingAccounts={loadingAccounts || connectingToChannel}
          runningAccountIds={runningAccountIds}
        />
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        {isFeatureLimited && (
          <FeatureLimitedProgressBar
            data={{ currentCount, limit }}
            upgradeActionOrigin={UPGRADE_DIALOG_ACTIONS_ORIGIN.cipPageConnectDialog}
            connectedAccounts={connectedAccounts}
          />
        )}
        {isAccountProcessing && !isFeatureLimited && (
          <>
            <CircularProgress size={16} />
            <Typography className={styles.processingMessage}>
              Processing may take up to 10 minutes. You can safely close this window.
            </Typography>
          </>
        )}
        <Button
          className={styles.buttonCancel}
          onClick={onClose}
          variant="text"
          color="primary"
          aria-label="Cancel"
          data-testid="AddAccountSelectionPopup__CancelButton"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AdAccountsSelectionPopup.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
AdAccountsSelectionPopup.defaultProps = {
  className: null,
  open: false,
};
